import { useCallback, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
const STORAGE_KEY = 'ecotone-theme';

/**
 * Light is the product default. We deliberately do NOT follow the OS setting on
 * first visit — the brand is light, and a dark-OS user landing in dark mode
 * unprompted was surprising. Dark is opt-in via the toggle, and once chosen it
 * persists.
 */
function defaultTheme(): Theme {
  return 'light';
}

function stored(): Theme | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

/** Applied before React mounts too — see applyThemeEarly. */
function apply(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
}

/**
 * Sets the theme class synchronously at startup so there's no flash of the
 * wrong palette before hydration. Called from main.tsx before render.
 */
export function applyThemeEarly() {
  apply(stored() ?? defaultTheme());
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => stored() ?? defaultTheme());

  useEffect(() => {
    apply(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  return { theme, toggle };
}
